import { browserStorage } from '../cache/browserStorage';
import { refreshAccessToken } from '../api';
import logger from '@upheal/utils/logger';
import axios from 'axios';
import { isWhiteLabelApp } from '@upheal/design-system/theme/legacy/is-whitelabel';

interface SessionValidationResult {
    isValid: boolean;
}

/**
 * Validates session for non-rememberMe users.
 *
 * When rememberMe=false, the refresh token is stored as a session cookie which
 * is cleared when the browser closes. However, the access token in localStorage
 * persists. This check detects this invalid state early rather than letting the
 * user work until the access token expires.
 *
 * @returns isValid: false if session is definitively invalid (should redirect)
 */
export const validateSessionOnInit =
    async (): Promise<SessionValidationResult> => {
        if (isWhiteLabelApp) {
            return { isValid: true };
        }

        const authToken = browserStorage.loadAuthToken();
        const shouldValidate = Boolean(
            authToken && !authToken.isClient && !authToken.rememberMe,
        );

        if (!shouldValidate) {
            return { isValid: true };
        }

        try {
            logger.debug('[auth] validating session for non-rememberMe user');
            await refreshAccessToken();
            return { isValid: true };
        } catch (error: unknown) {
            // refreshAccessToken() already handles auth failures (status >= 400) by:
            // 1. Clearing the auth token
            // 2. Triggering onRefreshTokenFailure callback (which redirects to login)
            // So we just check if the token was cleared to detect definitive failures.
            const wasAuthCleared = browserStorage.loadAuthToken() === null;

            if (wasAuthCleared) {
                logger.info('[auth] session validation failed, token cleared');
                return { isValid: false };
            }

            // Token still exists = transient error (network, 500, etc.)
            // Proceed normally and let existing interceptor handle refresh when needed.
            const status = axios.isAxiosError(error)
                ? error.response?.status
                : undefined;
            logger.warn(
                '[auth] session validation encountered transient error, proceeding',
                { status, error },
            );
            return { isValid: true };
        }
    };
